import { Template } from '@phrasee/phrasee-typings'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { errorToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'
import useSelectedComponentElement from 'common/hooks/useSelectedComponentElement'
import useVariantsToApproveQuery from 'features/unifiedFlow/contentPage/hooks/useVariantsToApproveQuery'

import { isComponentElement } from '../interfaces'
import { contentKeys } from '../queryKeys'
import { deletePersonalizedVariant } from '..'

const useDeletePersonalizedVariantMutation = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const queryClient = useQueryClient()
  const { invalidateAll: invalidateAllVariantsToApproveCountQuery } =
    useVariantsToApproveQuery()

  const { data: selectedElement } = useSelectedComponentElement()
  const region =
    selectedElement?.personalization_settings?.configuration.privacy_region

  return useMutation({
    mutationFn: deletePersonalizedVariant,
    onError: () => {
      errorToast('Something went wrong. Please try again.')
    },
    onMutate: (variables) => {
      queryClient.setQueryData<Template>(
        contentKeys.content(variables.contentId),
        (old) => {
          if (!old) {
            return old
          }
          return {
            ...old,
            elements: old.elements.map((element) => {
              if (
                element.element_id !== variables.elementId ||
                !isComponentElement(element) ||
                !element.personalized_variants
              ) {
                return element
              }

              return {
                ...element,
                personalized_variants: element.personalized_variants.map(
                  (variant) => {
                    if (variant.variant_id === variables.subjectLineId) {
                      return {
                        ...variant,
                        isLoading: true,
                      }
                    }
                    return variant
                  }
                ),
              }
            }),
          }
        }
      )
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contentKeys.content(variables.contentId),
      })
      invalidateAllVariantsToApproveCountQuery()
      if (selectedElement?.personalization_id) {
        queryClient.invalidateQueries({
          queryKey: contentKeys.variableCombinations({
            personalizationId: selectedElement?.personalization_id,
            region,
            accountId,
          }),
        })
      }
    },
  })
}

export default useDeletePersonalizedVariantMutation
